import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { capture } from '../lib/posthog';
import { trackStripeCheckoutLaunched } from '../lib/trialAnalytics';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, X, Loader2 } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import cTraderChoicesLogo from '../assets/ctrader_choices_logo.png';
import tradelockerChoicesLogo from '../assets/tradelocker_choices_logo.png';
import tradovateChoicesLogo from '../assets/tradovate_choices_logo.png';
import metatrader4ChoicesLogo from '../assets/metatrader4_choices_logo.png';
import metatrader5ChoicesLogo from '../assets/metatrader5_choices_logo.png';
import ninjatraderChoicesLogo from '../assets/ninjatrader_choices_logo.png';
import ftmoLogo from '../assets/brokers/ftmo.png';
import apexLogo from '../assets/brokers/apex.webp';
import topOneLogo from '../assets/brokers/topone.webp';
import topStepLogo from '../assets/brokers/topstep.png';
import tradezellaLogo from '../assets/brokers/tradezella.png';
import ufundedLogo from '../assets/brokers/ufunded.webp';
import { useAuth } from '../contexts/AuthContext';
import { getCustomerPortalSession } from '../services/stripe';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { affiliateService } from '../services/affiliateService';
import { getAffiliateCookie } from '../utils/affiliateCookies';
import { getAffiliateFromStorage, saveAffiliateToStorage, clearAffiliateStorage } from '../utils/affiliateStorage';
import { usePricingTracking } from '../hooks/usePricingTracking';
import trustpilotLogo from '../assets/trustpilot.svg';
import avisTrustpilot1Png from '../assets/webp-landing/avis-trustpilot-1.webp';
import avisTrustpilot2Png from '../assets/webp-landing/avis-trustpilot-2.webp';
import avisTrustpilot3Png from '../assets/webp-landing/avis-trustpilot-3.webp';
import avisTrustpilot4Png from '../assets/webp-landing/avis-trustpilot-4.webp';
import avisTrustpilot5Png from '../assets/webp-landing/avis-trustpilot-5.webp';
import avisTrustpilot6Png from '../assets/webp-landing/avis-trustpilot-6.webp';
import atlasMainPng from '../assets/atlas-main.png';
import coinIcon from '../assets/coin.png';
import partenaireImg1Png from '../assets/webp-landing/partenaire-img-1.webp';
import partenaireImg2Png from '../assets/webp-landing/partenaire-img-2.webp';
import partenaireImg3Png from '../assets/webp-landing/partenaire-img-3.webp';
import partenaireImg1WebP from '../assets/webp-landing/partenaire-img-1.webp';
import partenaireImg2WebP from '../assets/webp-landing/partenaire-img-2.webp';
import partenaireImg3WebP from '../assets/webp-landing/partenaire-img-3.webp';
import { PricingPlanCards } from '../components/pricing/PricingPlanCards';
import {
  PricingPremiumOfferCard,
  PRICING_PREMIUM_AMOUNTS,
  formatEuro,
} from '../components/pricing/PricingPremiumOfferCard';
import { getPricingCtaState, getPricingCardFootnotes, getPricingStickyMobileLabels, STRIPE_BILLING_PORTAL, redirectToPremiumStripeCheckout } from '../utils/pricingCtaLogic';

// Composant AnimatedText pour les animations
const AnimatedText = React.memo(({ 
  children,
  delay = 0,
  duration = 800
}: { 
  children: React.ReactNode;
  delay?: number;
  duration?: number;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Intersection Observer pour déclencher l'animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [delay, isVisible]);

  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`
      }}
    >
      {children}
    </div>
  );
});

// Composant pour animer les prix
const AnimatedPrice: React.FC<{ value: string; interval: 'monthly' | 'yearly' }> = ({ value, interval }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const prevIntervalRef = useRef(interval);
  
  useEffect(() => {
    if (prevIntervalRef.current === interval) {
      setDisplayValue(value);
      return;
    }
    
    // Animation du prix
    const startValue = parseFloat(displayValue.replace('€', '').replace(',', '.'));
    const endValue = parseFloat(value.replace('€', '').replace(',', '.'));
    const duration = 600; // durée de l'animation en ms
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = startValue + (endValue - startValue) * easeOut;
      setDisplayValue(currentValue.toFixed(2).replace('.', ',') + '€');
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
        prevIntervalRef.current = interval;
      }
    };
    
    requestAnimationFrame(animate);
  }, [value, interval]);
  
  return <>{displayValue}</>;
};

interface PricingPartner {
  id: string;
  name: string;
  bio: string;
  subscribers: number;
  localPhoto: string;
  localPhotoWebP: string;
  isPrimary?: boolean;
}

// Bio traduite via `public.pricing.partners.bios.<id>` — le reste (nom, photo,
// nombre d'abonnés) n'est pas une donnée linguistique.
const pricingPartnersBase: Omit<PricingPartner, 'bio'>[] = [
  {
    id: 'hugofx',
    name: 'HugoFX',
    subscribers: 152000,
    localPhoto: partenaireImg1Png,
    localPhotoWebP: partenaireImg1WebP,
    isPrimary: true,
  },
  {
    id: 'chloefx',
    name: 'ChloéFX',
    subscribers: 19000,
    localPhoto: partenaireImg3Png,
    localPhotoWebP: partenaireImg3WebP,
  },
  {
    id: 'zefrenchtrader',
    name: 'ZeFrenchTrader',
    subscribers: 7000,
    localPhoto: partenaireImg2Png,
    localPhotoWebP: partenaireImg2WebP,
  },
];

const pricingReviews = [
  { img: avisTrustpilot1Png, name: 'Max Mntl', title: 'Excellent pour un trader rentable', text: "Un Saas performant qui permet d'avoir beaucoup de data sur son trading afin de prendre de meilleures décisions grâce à l'analyse IA." },
  { img: avisTrustpilot2Png, name: 'Melvyn Bouyer', title: 'Best journal de trading', text: 'Import automatique de vos trades avec notes et photos. Une IA pour analyser vos difficultés et points forts. LA solution pour tout trader.' },
  { img: avisTrustpilot3Png, name: 'Charles Benoit', title: 'Le meilleur outil indispensable', text: "On peut absolument tout analyser grâce à l'IA, qui nous indique ce qu'il faut améliorer pour être meilleur." },
  { img: avisTrustpilot4Png, name: 'Vainina Colombel', title: 'Meilleur journal de trading', text: "Sans hésitation le meilleur journal de trading qui existe ! Simple d'utilisation, tout est bien expliqué. Merci pour cet outil si précieux." },
  { img: avisTrustpilot5Png, name: 'Cyril', title: "La nécessité d'avoir un journal de trading", text: "La nécessité d'avoir un journal de trading est clairement obligatoire et la solution FocusPips permet enfin de gagner du temps sur l'insertion automatique de ces trades (Metatrader, Ctrader lors de ces challenges) et l'analyse pointue et au top de son trading à travers le temps. Tout ça dans une interface claire et lisible, un must have pour tous ceux qui veulent optimiser et performer. L'outil est hyper intuitif, les tableaux de bord permettent directement de savoir ou on en est et surtout ce qui fonctionne dans son trading. Je recommande fortement" },
  { img: avisTrustpilot6Png, name: 'Johann Valerius', title: 'Tout en 1', text: "Ce journal de trading se démarque par sa simplicité d'utilisation, son efficacité dans le suivi des performances, et surtout un prix très compétitif qui le rend accessible à tous." },
];

const PRICING_FEATURE_GRADIENT_ID = 'pricing-feature-lock-gradient';

const PricingFeatureX = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 flex-shrink-0" style={{ color: '#9ca3af' }}><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
);

const PricingFeatureCheck = () => (
  <Check className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" style={{ color: '#9b87f5' }} />
);

const PricingFeatureLock = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" aria-hidden="true">
    <defs>
      <linearGradient id={PRICING_FEATURE_GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8B5CF6" />
        <stop offset="50%" stopColor="#EC4899" />
        <stop offset="100%" stopColor="#FB923C" />
      </linearGradient>
    </defs>
    <path d="M12 13.25C12.4142 13.25 12.75 13.5858 12.75 14V18C12.75 18.4142 12.4142 18.75 12 18.75C11.5858 18.75 11.25 18.4142 11.25 18V14C11.25 13.5858 11.5858 13.25 12 13.25Z" fill={`url(#${PRICING_FEATURE_GRADIENT_ID})`} />
    <path fillRule="evenodd" clipRule="evenodd" d="M5.25 9.30277V8C5.25 4.27208 8.27208 1.25 12 1.25C15.7279 1.25 18.75 4.27208 18.75 8V9.30277C18.9768 9.31872 19.1906 9.33948 19.3918 9.36652C20.2919 9.48754 21.0497 9.74643 21.6517 10.3483C22.2536 10.9503 22.5125 11.7081 22.6335 12.6082C22.75 13.4752 22.75 14.5775 22.75 15.9451V16.0549C22.75 17.4225 22.75 18.5248 22.6335 19.3918C22.5125 20.2919 22.2536 21.0497 21.6517 21.6516C21.0497 22.2536 20.2919 22.5125 19.3918 22.6335C18.5248 22.75 17.4225 22.75 16.0549 22.75H7.94513C6.57754 22.75 5.47522 22.75 4.60825 22.6335C3.70814 22.5125 2.95027 22.2536 2.34835 21.6516C1.74643 21.0497 1.48754 20.2919 1.36652 19.3918C1.24996 18.5248 1.24998 17.4225 1.25 16.0549V15.9451C1.24998 14.5775 1.24996 13.4752 1.36652 12.6082C1.48754 11.7081 1.74643 10.9503 2.34835 10.3483C2.95027 9.74643 3.70814 9.48754 4.60825 9.36652C4.80938 9.33948 5.02317 9.31872 5.25 9.30277ZM6.75 8C6.75 5.10051 9.10051 2.75 12 2.75C14.8995 2.75 17.25 5.10051 17.25 8V9.25344C16.8765 9.24999 16.4784 9.24999 16.0549 9.25H7.94513C7.52161 9.24999 7.12353 9.24999 6.75 9.25344V8ZM4.80812 10.8531C4.07435 10.9518 3.68577 11.1322 3.40901 11.409C3.13225 11.6858 2.9518 12.0743 2.85315 12.8081C2.75159 13.5635 2.75 14.5646 2.75 16C2.75 17.4354 2.75159 18.4365 2.85315 19.1919C2.9518 19.9257 3.13225 20.3142 3.40901 20.591C3.68577 20.8678 4.07435 21.0482 4.80812 21.1469C5.56347 21.2484 6.56459 21.25 8 21.25H16C17.4354 21.25 18.4365 21.2484 19.1919 21.1469C19.9257 21.0482 20.3142 20.8678 20.591 20.591C20.8678 20.3142 21.0482 19.9257 21.1469 19.1919C21.2484 18.4365 21.25 17.4354 21.25 16C21.25 14.5646 21.2484 13.5635 21.1469 12.8081C21.0482 12.0743 20.8678 11.6858 20.591 11.409C20.3142 11.1322 19.9257 10.9518 19.1919 10.8531C18.4365 10.7516 17.4354 10.75 16 10.75H8C6.56459 10.75 5.56347 10.7516 4.80812 10.8531Z" fill={`url(#${PRICING_FEATURE_GRADIENT_ID})`} />
  </svg>
);

const shortenPricingFeatureLabelForMobile = (label: string): string | null => {
  if (label.includes(' — ')) {
    return label.split(' — ')[0].trim();
  }
  if (label.includes(' + ')) {
    return label.split(' + ')[0].trim();
  }
  if (label.startsWith('Jusqu\'à ')) {
    return label.slice('Jusqu\'à '.length);
  }
  if (label === 'Sessions illimitées') {
    return 'Illimité';
  }
  if (label === 'Comptes illimités') {
    return 'Illimités';
  }
  return null;
};

const PricingFeatureText = ({ children, multiline = false, compact = false }: { children: React.ReactNode; multiline?: boolean; compact?: boolean }) => {
  const text = typeof children === 'string' ? children : null;
  const mobileLabel = text ? shortenPricingFeatureLabelForMobile(text) : null;
  const className = `block w-full text-center font-medium leading-snug whitespace-normal break-words ${
    compact ? 'text-[0.7rem] sm:text-lg' : 'text-xs sm:text-lg'
  } ${multiline ? '' : 'sm:whitespace-nowrap'}`;

  if (text && mobileLabel && mobileLabel !== text) {
    return (
      <>
        <span className={`${className} sm:hidden`} style={{ color: '#454653' }}>
          {mobileLabel}
        </span>
        <span className={`${className} hidden sm:block`} style={{ color: '#454653' }}>
          {children}
        </span>
      </>
    );
  }

  return (
    <span className={className} style={{ color: '#454653' }}>
      {children}
    </span>
  );
};

const PricingFeatureCheckLabel = ({ children, multiline = false }: { children: React.ReactNode; multiline?: boolean }) => {
  const text = typeof children === 'string' ? children : null;
  const mobileLabel = text ? shortenPricingFeatureLabelForMobile(text) : null;
  const useShortMobile = Boolean(text && mobileLabel && mobileLabel !== text);

  return (
    <span className="flex w-full min-w-0 max-w-full flex-col items-center justify-center gap-1 sm:inline-flex sm:w-auto sm:flex-row sm:gap-1.5">
      <PricingFeatureCheck />
      <PricingFeatureText compact multiline={multiline && !useShortMobile}>{children}</PricingFeatureText>
    </span>
  );
};

type PricingFeatureCell = 'x' | 'check' | 'locked' | string;

interface PricingFeatureRow {
  title: string;
  description?: string;
  basic: PricingFeatureCell;
  premium: PricingFeatureCell;
}

interface PricingFeatureSection {
  title: string;
  rows: PricingFeatureRow[];
}

// La table de comparaison Basic/Premium vient intégralement de
// `public.pricing.features_table.sections` (namespace i18n) — voir son
// utilisation via `t(..., { returnObjects: true })` dans le composant.

const renderPricingFeatureCell = (cell: PricingFeatureCell, t: TFunction) => {
  if (cell === 'x') return <PricingFeatureX />;
  if (cell === 'check') return <PricingFeatureCheck />;
  if (cell === 'locked') {
    return (
      <span className="flex w-full min-w-0 flex-col items-center justify-center gap-1.5">
        <PricingFeatureLock />
        <PricingFeatureText compact multiline>
          {t('public.pricing.features_table.locked_line1')}
          <br />
          {t('public.pricing.features_table.locked_line2')}
        </PricingFeatureText>
      </span>
    );
  }
  if (cell.startsWith('✓ ')) {
    const label = cell.slice(2);
    return (
      <PricingFeatureCheckLabel multiline={label.includes('—') || label.includes('·') || label.includes('+')}>
        {label}
      </PricingFeatureCheckLabel>
    );
  }
  return <PricingFeatureText compact multiline={cell.includes('—') || cell.includes('·')}>{cell}</PricingFeatureText>;
};

export const Pricing: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation(); // Pour lire les paramètres de l'URL
  const {
    user
  } = useAuth();

  const pricingPartners: PricingPartner[] = useMemo(
    () => pricingPartnersBase.map((p) => ({ ...p, bio: t(`public.pricing.partners.bios.${p.id}`) })),
    [t],
  );

  const PRICING_FEATURE_SECTIONS = t('public.pricing.features_table.sections', { returnObjects: true }) as PricingFeatureSection[];

  // `getPricingCtaState`/`getPricingStickyMobileLabels` renvoient un libellé FR
  // (legacy, appelants non traduits) + une clé i18n `landing.plans.cta*` — on
  // préfère toujours la clé quand elle existe.
  const trLabel = (key: string | undefined, fallback: string) => (key ? t(key) : fallback);

  const [loadingUpgrade, setLoadingUpgrade] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('yearly');
  const isHomePromo = document.cookie.split(';').some(c => c.trim() === 'home_promo=1');

  useEffect(() => {
    capture('pricing_page_viewed', {
      source: location.search || 'direct',
      is_home_promo: isHomePromo,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<string | null>(null);
  const [isTrial, setIsTrial] = useState(false);
  const [tryTrial, setTryTrial] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [partnerActive, setPartnerActive] = useState(0);
  const [partnerMobileIndex, setPartnerMobileIndex] = useState(pricingPartners.length);
  const [partnerMobileTransitionEnabled, setPartnerMobileTransitionEnabled] = useState(true);

  // NOUVEAU: État pour stocker l'ID d'affilié validé et les liens
  const [validatedAffiliateId, setValidatedAffiliateId] = useState<string | null>(null);
  const [, setAffiliateName] = useState<string | null>(null);
  const [affiliateLinks, setAffiliateLinks] = useState<{
    premiumMonthly: string;
    premiumAnnual: string;
    basicMonthly: string;
    basicAnnual: string;
  } | null>(null);
  const [, setAffiliateCode] = useState<string | null>(null);
  
  // État pour visualiser les dead clicks depuis l'admin
  const [debugClick, setDebugClick] = useState<{ x: number; y: number } | null>(null);

  // Tracking complet du comportement utilisateur sur la page pricing
  const {
    trackCTAHover,
    trackToggle,
    trackFAQ,
  } = usePricingTracking(user?.uid || null);

  // Détection du mode debug pour visualiser les dead clicks
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isDebug = params.get('debug-click') === 'true';
    const x = params.get('x');
    const y = params.get('y');
    
    if (isDebug && x && y) {
      setDebugClick({ x: parseInt(x), y: parseInt(y) });
      
      // Scroll vers la position après un court délai
      setTimeout(() => {
        window.scrollTo({
          top: parseInt(y) - window.innerHeight / 2,
          behavior: 'smooth'
        });
      }, 500);
    }
  }, [location.search]);

  // Gestion du header sticky fixe avec animation
  useEffect(() => {
    const handleScroll = () => {
      const headerInitial = document.getElementById('pricing-header-initial');
      const stickyHeader = document.getElementById('pricing-sticky-header');
      const faqSection = document.getElementById('pricing-faq-section');
      
      if (headerInitial && stickyHeader) {
        const rect = headerInitial.getBoundingClientRect();
        const faqRect = faqSection?.getBoundingClientRect();
        
        // Masquer si on atteint la section FAQ
        if (faqRect && faqRect.top <= 100) {
          stickyHeader.style.opacity = '0';
          stickyHeader.style.transform = 'translateY(-10px)';
          stickyHeader.style.pointerEvents = 'none';
        }
        // Afficher quand le header initial sort de l'écran
        else if (rect.bottom <= 0) {
          stickyHeader.style.opacity = '1';
          stickyHeader.style.transform = 'translateY(0)';
          stickyHeader.style.pointerEvents = 'auto';
        } 
        // Masquer dans les autres cas
        else {
          stickyHeader.style.opacity = '0';
          stickyHeader.style.transform = 'translateY(-10px)';
          stickyHeader.style.pointerEvents = 'none';
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Gestion du sticky header pour la section des cards
  useEffect(() => {
    const handleCardsScroll = () => {
      const cardsToggle = document.getElementById('cards-toggle-initial');
      const cardsStickyHeader = document.getElementById('cards-sticky-header');
      const featuresSection = document.getElementById('pricing-header-initial');
      
      if (cardsToggle && cardsStickyHeader) {
        const rect = cardsToggle.getBoundingClientRect();
        const featuresRect = featuresSection?.getBoundingClientRect();
        
        // Masquer si on atteint la section features
        if (featuresRect && featuresRect.top <= 100) {
          cardsStickyHeader.style.opacity = '0';
          cardsStickyHeader.style.transform = 'translateY(-10px)';
          cardsStickyHeader.style.pointerEvents = 'none';
        }
        // Afficher quand le toggle initial sort de l'écran
        else if (rect.bottom <= 0) {
          cardsStickyHeader.style.opacity = '1';
          cardsStickyHeader.style.transform = 'translateY(0)';
          cardsStickyHeader.style.pointerEvents = 'auto';
        } 
        // Masquer dans les autres cas
        else {
          cardsStickyHeader.style.opacity = '0';
          cardsStickyHeader.style.transform = 'translateY(-10px)';
          cardsStickyHeader.style.pointerEvents = 'none';
        }
      }
    };

    window.addEventListener('scroll', handleCardsScroll);
    handleCardsScroll(); // Check initial state
    
    return () => window.removeEventListener('scroll', handleCardsScroll);
  }, []);

  // Gestion du badge "2 mois offerts" - Badge affiché tout le temps
  // useEffect(() => {
  //   if (billingInterval === 'yearly') {
  //     setShowBadge(true);
  //     setIsBadgeFadingOut(false);
  //   } else {
  //     setIsBadgeFadingOut(true);
  //     const timer = setTimeout(() => {
  //       setShowBadge(false);
  //       setIsBadgeFadingOut(false);
  //     }, 300); // Durée de l'animation fadeOut
  //     return () => clearTimeout(timer);
  //   }
  // }, [billingInterval]);

  // NOUVEAU: Effet pour récupérer et valider l'ID d'affilié
  useEffect(() => {
    const validateAndSetAffiliateId = async () => {
      const queryParams = new URLSearchParams(location.search);
      const affIdFromUrl = queryParams.get('ref');
      const codeFromUrl = queryParams.get('code');

      if (affIdFromUrl) {
        try {
          // Valider l'ID via le backend
          const validationResult = await affiliateService.validateAffiliateId(affIdFromUrl);

          if (validationResult?.isValid) {
            // L'ID est valide
            setValidatedAffiliateId(affIdFromUrl);
            setAffiliateName(validationResult.affiliateName || null);

            // Récupérer les liens d'affiliation
            const links = await affiliateService.getAffiliateLinks(affIdFromUrl);
            if (links) {
              setAffiliateLinks(links);
            }

            // Sauvegarder dans le localStorage avec expiration de 30 jours
            saveAffiliateToStorage({
              affiliateId: affIdFromUrl,
              affiliateName: validationResult.affiliateName
            });
            
            setAffiliateCode(codeFromUrl ? codeFromUrl.toUpperCase() : null);
          } else {
            // L'ID n'est pas valide, on le supprime
            clearAffiliateStorage();
            setValidatedAffiliateId(null);
            setAffiliateLinks(null);
            setAffiliateName(null);
            setAffiliateCode(null);
          }
        } catch (error) {
          clearAffiliateStorage();
          setValidatedAffiliateId(null);
          setAffiliateName(null);
          setAffiliateCode(null);
        }
      } else {
        // Pas d'ID dans l'URL: vérifier le localStorage puis les cookies
        const storageData = getAffiliateFromStorage();
        
        if (storageData) {
          // Données trouvées dans le localStorage et encore valides (moins de 30 jours)
          try {
            // Valider l'ID pour s'assurer qu'il est toujours actif côté backend
            const validationResult = await affiliateService.validateAffiliateId(storageData.affiliateId);
            
            if (validationResult?.isValid) {
              setValidatedAffiliateId(storageData.affiliateId);
              setAffiliateName(storageData.affiliateName || validationResult.affiliateName || null);
              
              // Récupérer les liens d'affiliation
              const links = await affiliateService.getAffiliateLinks(storageData.affiliateId);
              if (links) {
                setAffiliateLinks(links);
              }
              
              setAffiliateCode(codeFromUrl ? codeFromUrl.toUpperCase() : null);
            } else {
              // L'affilié n'est plus valide côté backend, nettoyer
              clearAffiliateStorage();
              setValidatedAffiliateId(null);
              setAffiliateLinks(null);
              setAffiliateName(null);
              setAffiliateCode(codeFromUrl ? codeFromUrl.toUpperCase() : null);
            }
          } catch (error) {
            // Erreur de validation, garder les données du storage quand même
            setValidatedAffiliateId(storageData.affiliateId);
            setAffiliateName(storageData.affiliateName || null);
            setAffiliateCode(codeFromUrl ? codeFromUrl.toUpperCase() : null);
          }
        } else {
          // Pas de données dans le localStorage, vérifier les cookies
          const affiliateCookie = getAffiliateCookie();
          
          if (affiliateCookie) {
            try {
              // Valider l'ID du cookie
              const validationResult = await affiliateService.validateAffiliateId(affiliateCookie.affiliateId);
              
              if (validationResult?.isValid) {
                setValidatedAffiliateId(affiliateCookie.affiliateId);
                setAffiliateName(affiliateCookie.affiliateName || validationResult.affiliateName || null);
                
                // Sauvegarder aussi dans le localStorage pour la prochaine fois
                saveAffiliateToStorage({
                  affiliateId: affiliateCookie.affiliateId,
                  affiliateName: affiliateCookie.affiliateName || validationResult.affiliateName
                });
                
                // Récupérer les liens d'affiliation
                const links = await affiliateService.getAffiliateLinks(affiliateCookie.affiliateId);
                if (links) {
                  setAffiliateLinks(links);
                }
                
                setAffiliateCode(affiliateCookie.code ? affiliateCookie.code.toUpperCase() : codeFromUrl ? codeFromUrl.toUpperCase() : null);
              } else {
                // Cookie invalide, ne rien faire
                setValidatedAffiliateId(null);
                setAffiliateLinks(null);
                setAffiliateName(null);
                setAffiliateCode(codeFromUrl ? codeFromUrl.toUpperCase() : null);
              }
            } catch (error) {
              setValidatedAffiliateId(null);
              setAffiliateLinks(null);
              setAffiliateName(null);
              setAffiliateCode(codeFromUrl ? codeFromUrl.toUpperCase() : null);
            }
          }
          // Si ni localStorage ni cookie, ne rien faire - on ne supprime rien
        }
      }
    };
    
    validateAndSetAffiliateId();
  }, [location.search]);

  // Charger les données utilisateur si connecté (sans bloquer l'affichage)
  useEffect(() => {
    if (!user) return;
    const checkPremiumStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        setIsPremium(userData?.isPremium === true);
        setSubscriptionType(userData?.subscriptionType || null);
        setIsTrial(userData?.isTrial === true);
        setTryTrial(userData?.tryTrial === true);
      } catch (error) {
        // console.error supprimé
      }
    };
    checkPremiumStatus();
    
    const handleFocus = () => {
      checkPremiumStatus();
    };
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkPremiumStatus();
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const interval = setInterval(checkPremiumStatus, 30000);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [user]);

  const handlePortal = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      setLoadingUpgrade(true);
      const response = await getCustomerPortalSession(user.uid);
      if (response && response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      // console.error supprimé
    } finally {
      setLoadingUpgrade(false);
    }
  };

  const handleFreePlanCta = () => {
    if (user) {
      navigate('/dashboard');
      return;
    }
    navigate('/register');
  };

  const ctaState = useMemo(
    () =>
      getPricingCtaState({
        isLoggedIn: !!user,
        isPremium,
        isTrial,
        subscriptionType,
        triedTrial: tryTrial,
      }),
    [user, isPremium, isTrial, subscriptionType, tryTrial],
  );

  const cardFootnotes = useMemo(
    () => getPricingCardFootnotes(billingInterval, !!user),
    [billingInterval, user],
  );

  const stickyMobileLabels = useMemo(
    () =>
      getPricingStickyMobileLabels({
        isLoggedIn: !!user,
        isPremium,
        isTrial,
        subscriptionType,
        triedTrial: tryTrial,
      }),
    [user, isPremium, isTrial, subscriptionType, tryTrial],
  );

  const handleBasicCta = useCallback(() => {
    if (ctaState.basicAction === 'none') return;
    if (ctaState.basicAction === 'portal') {
      window.location.href = STRIPE_BILLING_PORTAL;
      return;
    }
    handleFreePlanCta();
  }, [ctaState.basicAction]);

  const handlePremiumCta = useCallback(() => {
    if (ctaState.premiumAction === 'none') return;
    if (ctaState.premiumAction === 'portal') {
      window.location.href = STRIPE_BILLING_PORTAL;
      return;
    }
    if (ctaState.premiumAction === 'register') {
      navigate('/register');
      return;
    }

    if (user && isPremium && !isTrial) {
      handlePortal();
      return;
    }

    trackStripeCheckoutLaunched({
      source: 'pricing_page',
      modal: 'none',
      plan: 'premium',
      billing_interval: billingInterval,
      is_trial: isTrial,
    });

    setLoadingUpgrade(true);
    redirectToPremiumStripeCheckout(billingInterval, user?.email, user?.uid);
    setLoadingUpgrade(false);
  }, [ctaState.premiumAction, navigate, billingInterval, user, isPremium, isTrial]);

  // Avis pour le slider
  const testimonials = [
    {
      initials: "L",
      name: "Laurence (BE)",
      role: "",
      text: "Le top du top ! Un journal de trading ultra performant, simple et super ergonomique. Indispensable !"
    },
    {
      initials: "JY",
      name: "Jouan Yann (FR)",
      role: "",
      text: "Outil indispensable ! Stats, IA, gestion multi-comptes et support au top. Bravo à l'équipe FocusPips !"
    },
    {
      initials: "VC",
      name: "Vainina Colombel (PF)",
      role: "",
      text: "Sans hésitation le meilleur journal de trading ! Clair, intuitif et ultra pratique au quotidien."
    },
    {
      initials: "PF",
      name: "Pierre F. (FR)",
      role: "",
      text: "Fidèle utilisateur de FocusPips depuis ses débuts, je peux affirmer que c'est le journal de trading le plus complet et le plus intuitif du marché. L'import automatique des trades fait gagner un temps fou, les analyses IA permettent de comprendre ses erreurs et d'améliorer sa rentabilité, et le tout à un prix imbattable. L'équipe est à l'écoute, réactive et passionnée. Un vrai must-have pour tout trader sérieux !"
    }
  ];

  // Slider automatique pour les avis
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000); // Change tous les 5 secondes

    return () => clearInterval(interval);
  }, [currentTestimonial, testimonials.length]);

  const partnerTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const partnerMobileResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMobilePartnerViewport = () => typeof window !== 'undefined' && window.innerWidth < 640;
  const formatSubs = (n: number) => (n >= 1000 ? `${Math.floor(n / 1000)}k` : `${n}`);

  const recenterPartnerMobileTrack = (activeIndex: number) => {
    if (partnerMobileResetRef.current) clearTimeout(partnerMobileResetRef.current);
    partnerMobileResetRef.current = setTimeout(() => {
      setPartnerMobileTransitionEnabled(false);
      setPartnerMobileIndex(pricingPartners.length + activeIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setPartnerMobileTransitionEnabled(true));
      });
      partnerMobileResetRef.current = null;
    }, 560);
  };

  const partnerGoTo = (index: number) => {
    setPartnerActive(index);
    if (isMobilePartnerViewport()) {
      setPartnerMobileTransitionEnabled(true);
      setPartnerMobileIndex(pricingPartners.length + index);
    }
  };

  useEffect(() => {
    if (pricingPartners.length <= 1) return;
    partnerTimerRef.current = setInterval(() => {
      if (isMobilePartnerViewport()) {
        setPartnerMobileTransitionEnabled(true);
        setPartnerMobileIndex((prev) => prev + 1);
        setPartnerActive((prev) => {
          const next = (prev + 1) % pricingPartners.length;
          recenterPartnerMobileTrack(next);
          return next;
        });
        return;
      }
      setPartnerActive((prev) => (prev + 1) % pricingPartners.length);
    }, 4000);

    return () => {
      if (partnerTimerRef.current) clearInterval(partnerTimerRef.current);
      if (partnerMobileResetRef.current) clearTimeout(partnerMobileResetRef.current);
    };
  }, []);

  return (
    <>
      <style>{`
        body {
          overflow-x: hidden !important;
        }
        
        .pricing-cta-button {
          position: relative;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .pricing-cta-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .pricing-cta-button:active:not(:disabled) {
          transform: translateY(0px);
        }
        
        .premium-cta-button {
          position: relative;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .premium-cta-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .premium-cta-button:active:not(:disabled) {
          transform: translateY(0px);
        }
        
        @media (min-width: 1000px) {
          .price-annual {
            font-size: 2.10rem !important;
          }
          .price-monthly {
            font-size: 2.10rem !important;
          }
        }
        
        @keyframes progress {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }
        
        @keyframes gradientShift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        .bento-button {
          border: none;
          border-radius: 0.75rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .bento-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(139, 92, 246, 0.3);
        }

        @keyframes pricingMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        @keyframes partnerReviewScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(-50% - 8px)); }
        }

        #pricing-features-section {
          background: #000000 !important;
        }

        #pricing-features-section [style*="#000000"],
        #pricing-sticky-header [style*="#000000"] {
          color: #ffffff !important;
        }

        #pricing-features-section [style*="#454653"],
        #pricing-sticky-header [style*="#454653"] {
          color: #ffffff !important;
        }

        #pricing-features-section [style*="#9ca3af"],
        #pricing-sticky-header [style*="#9ca3af"] {
          color: rgba(255,255,255,0.45) !important;
        }

        #pricing-features-section [style*="#e5e7eb"] {
          border-color: rgba(255,255,255,0.08) !important;
        }

        #pricing-features-section .border,
        #pricing-features-section .border-b {
          border-color: rgba(255,255,255,0.08) !important;
        }

        #pricing-features-section .toggle-button-inactive {
          color: #ffffff !important;
        }

        #pricing-features-section h3,
        #pricing-features-section .grid span,
        #pricing-sticky-header h3,
        #pricing-sticky-header .grid span {
          color: #ffffff !important;
        }

        #pricing-features-section .grid p,
        #pricing-sticky-header .grid p {
          color: rgba(255,255,255,0.45) !important;
        }
      `}</style>
      <Navbar backgroundColor="#000000" showAnimations={false} position="relative" navigationVariant="landing" isDark={true} />
    <section className="pt-8 px-4 sm:px-6 md:px-12 lg:px-24 relative overflow-hidden" style={{ background: '#000000', paddingBottom: '0' }}>

      <div className="max-w-[1920px] mx-auto relative">
        <style>{`
          .force-white-title {
            color: #ffffff !important;
          }
          
          .force-white-title * {
            -webkit-user-select: text !important;
            user-select: text !important;
          }
          
          .force-white-title::selection,
          .force-white-title *::selection {
            background-color: rgba(99, 102, 241, 0.5) !important;
          }

          /* Desktop : le titre tient sur une seule ligne, la taille se réduit
             juste ce qu'il faut pour ne jamais déborder de la largeur utile. */
          @media (min-width: 1024px) {
            .pricing-hero-title {
              white-space: nowrap;
              font-size: min(3.75rem, 4.4vw);
            }
          }
        `}</style>
        <div className="text-center max-w-6xl mx-auto">
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '5px 14px 5px 10px', borderRadius: '100px',
              background: 'rgba(255,255,255,0.05)',
              marginBottom: '36px',
              animation: 'fadeInUp 0.6s ease-out both',
            }}
          >
            <img src={atlasMainPng} alt="Atlas" style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }} />
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', fontWeight: 500 }}>
              {t('public.pricing.hero.badge')}
            </span>
          </div>

          <h1
            className="force-white-title pricing-hero-title text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
            style={{ animation: 'fadeInUp 0.8s ease-out 0.2s both' }}
          >
            {t('public.pricing.hero.title_prefix')} <span
              style={{
                backgroundImage: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >{t('public.pricing.hero.title_highlight')}</span>
          </h1>

          <p
            className="text-base sm:text-lg lg:text-xl mb-4 max-w-3xl mx-auto"
            style={{
              color: '#9ca3af',
              userSelect: 'text',
              WebkitUserSelect: 'text',
              animation: 'fadeInUp 0.8s ease-out 0.4s both'
            }}
          >
            {t('public.pricing.hero.subtitle')}
          </p>

          <div
            className="mb-6"
            style={{ animation: 'fadeInUp 0.8s ease-out 0.5s both', textAlign: 'center' }}
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-[10px]">
              <div className="flex items-center justify-center gap-[10px]">
                <img src={trustpilotLogo} alt="Trustpilot stars" style={{ height: 20, width: 'auto', flexShrink: 0 }} />
                <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700 }}>{t('public.pricing.hero.trustpilot_rating')}</span>
              </div>
              <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.83rem' }}>
                • <span style={{ color: 'rgba(255,255,255,0.72)', fontWeight: 600 }}>{t('public.pricing.hero.social_proof_count')}</span> {t('public.pricing.hero.social_proof_suffix')}
              </span>
            </div>
          </div>
          
        </div>

        <div className="mt-8 flex flex-col items-center gap-3">
          <style>{`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          
            @keyframes fadeInRight {
              from {
                opacity: 0.7;
                transform: translateX(-5px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            
            @keyframes fadeInLeft {
              from {
                opacity: 0.7;
                transform: translateX(5px);
              } 
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            
            .toggle-monthly-active { 
              animation: fadeInLeft 0.4s ease-out;
            }
            
            .toggle-yearly-active {
              animation: fadeInRight 0.4s ease-out;
            }
            
            .toggle-button-inactive {
              color: #ffffff !important;
            }

            .toggle-button-active {
              color: white !important;
            }

            /* Toggle du hero : pastille active blanche, texte sombre. */
            .pricing-toggle-active {
              color: #0a0a0d !important;
            }

            .pricing-toggle-inactive {
              color: rgba(255, 255, 255, 0.7) !important;
            }

            .badge-fade-in {
              animation: fadeIn 0.3s ease-in;
            }
            
            .badge-fade-out {
              animation: fadeOut 0.3s ease-out;
            }
            
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            
            @keyframes fadeOut {
              from { opacity: 1; }
              to { opacity: 0; }
            }
          `}</style>

          <div id="cards-toggle-initial" className="relative inline-flex flex-col sm:flex-row items-center" style={{ animation: 'fadeInUp 0.8s ease-out 0.6s both', zIndex: 9999 }}>
            <div className="relative inline-flex items-center rounded-full p-1.5" style={{ backgroundColor: '#1a1a24', zIndex: 9999 }}>
              <button
                type="button"
                className={`relative px-7 py-2.5 text-sm whitespace-nowrap rounded-full focus:outline-none ${
                  billingInterval === 'monthly'
                    ? 'pricing-toggle-active toggle-monthly-active font-bold'
                    : 'pricing-toggle-inactive font-medium'
                }`}
                style={{
                  border: 'none',
                  outline: 'none',
                  backgroundColor: billingInterval === 'monthly' ? '#ffffff' : 'transparent'
                }}
                onClick={() => {
                  setBillingInterval('monthly');
                  trackToggle();
                }}
              >
                {t('public.pricing.toggle.monthly')}
              </button>
              <button
                type="button"
                className={`relative px-7 py-2.5 text-sm whitespace-nowrap rounded-full focus:outline-none ${
                  billingInterval === 'yearly'
                    ? 'pricing-toggle-active toggle-yearly-active font-bold'
                    : 'pricing-toggle-inactive font-medium'
                }`}
                style={{
                  border: 'none',
                  outline: 'none',
                  backgroundColor: billingInterval === 'yearly' ? '#ffffff' : 'transparent'
                }}
                onClick={() => {
                  setBillingInterval('yearly');
                  trackToggle();
                }}
              >
                {t('public.pricing.toggle.yearly_full')}
              </button>
            </div>
          </div>
        </div>

        <div
          className="mt-6 sm:mt-8 pb-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto lg:items-center"
          style={{ position: 'relative', zIndex: 1 }}
        >
          <PricingPlanCards
            basicOnly
            basicCtaPlacement="top"
            billingInterval={billingInterval}
            loadingUpgrade={loadingUpgrade}
            basicCtaLabel={trLabel(ctaState.basicLabelKey, ctaState.basicLabel)}
            premiumCtaLabel={trLabel(ctaState.premiumLabelKey, ctaState.premiumLabel)}
            onBasicClick={handleBasicCta}
            onPremiumClick={handlePremiumCta}
            basicDisabled={ctaState.basicDisabled}
            premiumDisabled={ctaState.premiumDisabled}
            hideBasicCta={ctaState.hideBasicCta}
            onBasicHover={(hover) => trackCTAHover('basic-cta-main', hover)}
          />

          <PricingPremiumOfferCard
            billingInterval={billingInterval}
            ctaLabel={trLabel(ctaState.premiumLabelKey, ctaState.premiumLabel)}
            onCtaClick={handlePremiumCta}
            ctaDisabled={ctaState.premiumDisabled}
            loading={loadingUpgrade}
            onCtaHover={(hover) => trackCTAHover('premium-cta-main', hover)}
          />
        </div>
      </div>

      {/* Sticky Header pour section Cards - Mobile uniquement */}
      <div 
        id="cards-sticky-header"
        className="sm:hidden"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          paddingTop: '1rem',
          paddingBottom: '1rem',
          opacity: 0,
          transform: 'translateY(-10px)',
          transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
          pointerEvents: 'none'
        }}
      >
        <div className="flex justify-center">
          <div className="inline-flex items-center rounded-full p-1 border" style={{ backgroundColor: '#121212', borderWidth: '1px', borderStyle: 'solid', borderColor: 'rgba(255, 255, 255, 0.08)' }}>
            <button
              type="button"
              className={`relative px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-full focus:outline-none ${
                billingInterval === 'monthly'
                  ? 'toggle-button-active toggle-monthly-active'
                  : 'toggle-button-inactive'
              }`}
              style={{ 
                border: 'none',
                outline: 'none',
                color: '#FFFFFF',
                ...(billingInterval === 'monthly' ? {
                  background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                  } : {})
              }}
              onClick={() => setBillingInterval('monthly')}
            >
              {t('public.pricing.toggle.monthly')}
            </button>
            <button
              type="button"
              className={`relative px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-full focus:outline-none ${
                billingInterval === 'yearly'
                  ? 'toggle-button-active toggle-yearly-active'
                  : 'toggle-button-inactive'
              }`}
              style={{ 
                border: 'none',
                outline: 'none',
                color: '#FFFFFF',
                ...(billingInterval === 'yearly' ? {
                  background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                  } : {})
              }}
              onClick={() => setBillingInterval('yearly')}
            >
              {t('public.pricing.toggle.yearly_full')}
            </button>
          </div>
        </div>
      </div>

      {/* Fixed Header - visible uniquement dans la section features */}
      <div 
        id="pricing-sticky-header"
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0,
          right: 0,
          zIndex: 50,
          background: '#000000',
          paddingTop: '1.25rem',
          paddingBottom: '1.25rem',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.45)',
          opacity: 0,
          transform: 'translateY(-10px)',
          transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
          pointerEvents: 'none'
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="flex flex-col lg:grid lg:grid-cols-[2fr_1fr_1fr] gap-4 lg:gap-8 lg:items-start">
            <div className="flex justify-center w-full lg:justify-center">
              <div className="inline-flex items-center rounded-full p-1 border w-fit" style={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'rgba(255, 255, 255, 0.08)' }}>
                <button
                  type="button"
                  className={`relative px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-full focus:outline-none ${
                    billingInterval === 'monthly'
                      ? 'toggle-button-active toggle-monthly-active'
                      : 'toggle-button-inactive'
                  }`}
                  style={{ 
                    border: 'none',
                    outline: 'none',
                    color: '#FFFFFF',
                    ...(billingInterval === 'monthly' ? {
                      background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                      } : {})
                  }}
                  onClick={() => setBillingInterval('monthly')}
                >
                  {t('public.pricing.toggle.monthly')}
                </button>
                <button
                  type="button"
                  className={`relative px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-full focus:outline-none ${
                    billingInterval === 'yearly'
                      ? 'toggle-button-active toggle-yearly-active'
                      : 'toggle-button-inactive'
                  }`}
                  style={{ 
                    border: 'none',
                    outline: 'none',
                    color: '#FFFFFF',
                    ...(billingInterval === 'yearly' ? {
                      background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                      } : {})
                  }}
                  onClick={() => setBillingInterval('yearly')}
                >
                  {t('public.pricing.toggle.yearly_full')}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 lg:contents">
            {/* Colonne Basic */}
            <div className="text-center lg:text-center">
              <div className="flex flex-col items-center justify-center gap-1 mb-1 lg:mb-2">
                <h3 className="text-base sm:text-lg lg:text-xl font-bold" style={{ color: '#454653' }}>BASIC</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl sm:text-4xl font-extrabold leading-none" style={{ color: '#454653' }}>
                    {t('public.pricing.plans.free_label')}
                  </span>
                </div>
              </div>
              {ctaState.hideBasicCta ? (
                <div className="h-[34px]" aria-hidden="true" />
              ) : (
                <button
                  className="w-full py-2 px-2 sm:px-4 rounded-lg border transition-all font-medium text-xs sm:text-sm whitespace-nowrap"
                  style={{ borderColor: 'rgba(255, 255, 255, 0.08)', color: '#FFFFFF', background: 'transparent' }}
                  onClick={handleBasicCta}
                  onMouseEnter={() => trackCTAHover('basic-cta-sticky', true)}
                  onMouseLeave={() => trackCTAHover('basic-cta-sticky', false)}
                  disabled={loadingUpgrade || ctaState.basicDisabled}
                >
                  {loadingUpgrade ? (
                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                  ) : (
                    <>
                      <span className="sm:hidden">{trLabel(stickyMobileLabels.basicLabelKey, stickyMobileLabels.basicLabel)}</span>
                      <span className="hidden sm:inline">{trLabel(ctaState.basicLabelKey, ctaState.basicLabel)}</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Colonne Premium */}
            <div className="text-center lg:text-center">
              <div className="flex flex-col items-center justify-center gap-1 mb-1 lg:mb-2">
                <h3 className="text-base sm:text-lg lg:text-xl font-bold">
                  <span 
                    style={{
                      backgroundImage: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >PREMIUM</span>
                </h3>
                <div className="flex items-baseline gap-1">
                  <span 
                    className="text-3xl sm:text-4xl font-extrabold leading-none"
                    style={{
                      backgroundImage: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {formatEuro(PRICING_PREMIUM_AMOUNTS[billingInterval === 'yearly' ? 'yearly' : 'monthly'])}
                  </span>
                  <span className="text-base sm:text-lg font-bold" style={{ color: '#9ca3af' }}>{t('public.pricing.plans.per_month_suffix')}</span>
                </div>
              </div>
              <button
                className="w-full bento-button py-2 px-2 sm:px-4 rounded-lg text-xs sm:text-sm whitespace-nowrap"
                style={{
                  background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                  }}
                onClick={handlePremiumCta}
                onMouseEnter={() => trackCTAHover('premium-cta-sticky', true)}
                onMouseLeave={() => trackCTAHover('premium-cta-sticky', false)}
                disabled={loadingUpgrade || ctaState.premiumDisabled}
              >
                {loadingUpgrade ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : (
                  <>
                    <span className="sm:hidden">{trLabel(stickyMobileLabels.premiumLabelKey, stickyMobileLabels.premiumLabel)}</span>
                    <span className="hidden sm:inline">{trLabel(ctaState.premiumLabelKey, ctaState.premiumLabel)}</span>
                  </>
                )}
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>
    </section>

      {/* ── Platform Carousel Section ── */}
      <section style={{ background: '#040405', padding: '80px 0 96px', position: 'relative', marginTop: '-1px', overflow: 'hidden' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto 48px', textAlign: 'center', padding: '0 24px' }}>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 20px' }}>{t('public.pricing.platforms.eyebrow')}</p>
          <h2 style={{ color: '#FFFFFF', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 20px' }}>
            {t('public.pricing.platforms.title_prefix')}{' '}
            <span style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('public.pricing.platforms.title_highlight')}</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 'clamp(0.8rem, 2.4vw, 1rem)', maxWidth: 'none', margin: '0 auto', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
            {t('public.pricing.platforms.subtitle')}
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
          <div style={{
            width: 'calc(100vw - 32px)',
            maxWidth: '700px',
            margin: '0 auto',
            overflow: 'hidden',
            maskImage: 'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
          }}>
            <div style={{ display: 'flex', gap: '36px', width: 'max-content', maxWidth: 'none', animation: 'pricingMarquee 30s linear infinite' }}>
              {[
                { logo: metatrader4ChoicesLogo, name: 'MT4' },
                { logo: metatrader5ChoicesLogo, name: 'MT5' },
                { logo: cTraderChoicesLogo, name: 'cTrader' },
                { logo: tradelockerChoicesLogo, name: 'TradeLocker' },
                { logo: tradovateChoicesLogo, name: 'Tradovate' },
                { logo: ninjatraderChoicesLogo, name: 'NinjaTrader' },
                { logo: metatrader4ChoicesLogo, name: 'MT4' },
                { logo: metatrader5ChoicesLogo, name: 'MT5' },
                { logo: cTraderChoicesLogo, name: 'cTrader' },
                { logo: tradelockerChoicesLogo, name: 'TradeLocker' },
                { logo: tradovateChoicesLogo, name: 'Tradovate' },
                { logo: ninjatraderChoicesLogo, name: 'NinjaTrader' },
              ].map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  <img src={p.logo} alt={p.name} style={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }} />
                  <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem', fontWeight: 600 }}>{p.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            width: 'calc(100vw - 32px)',
            maxWidth: '700px',
            margin: '0 auto',
            overflow: 'hidden',
            maskImage: 'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
          }}>
            <div style={{ display: 'flex', gap: '36px', width: 'max-content', maxWidth: 'none', animation: 'pricingMarquee 32s linear infinite reverse' }}>
              {[
                { logo: ftmoLogo, name: 'FTMO' },
                { logo: apexLogo, name: 'Apex' },
                { logo: topOneLogo, name: 'Top One' },
                { logo: topStepLogo, name: 'Topstep' },
                { logo: tradezellaLogo, name: 'Tradezella' },
                { logo: ufundedLogo, name: 'UFunded' },
                { logo: ftmoLogo, name: 'FTMO' },
                { logo: apexLogo, name: 'Apex' },
                { logo: topOneLogo, name: 'Top One' },
                { logo: topStepLogo, name: 'Topstep' },
                { logo: tradezellaLogo, name: 'Tradezella' },
                { logo: ufundedLogo, name: 'UFunded' },
              ].map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  <img src={p.logo} alt={p.name} style={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }} />
                  <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem', fontWeight: 600 }}>{p.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </section>

      {/* Feature Comparison Section */}
      <div 
        id="pricing-features-section"
        className="pb-16 sm:pb-24 -mx-4 sm:-mx-6 md:-mx-12 lg:-mx-24" 
        style={{ background: '#000000', width: '100vw', position: 'relative', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw' }}
      >
        
        {/* Header initial (visible avant scroll) */}
        <div id="pricing-header-initial" className="pt-8 sm:pt-16 pb-8 sm:pb-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
            <div
              style={{
                maxWidth: '760px',
                margin: '0 auto 32px',
                textAlign: 'center',
                padding: '0 24px',
              }}
            >
              <h2
                style={{
                  color: '#FFFFFF',
                  fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  margin: '0 0 16px',
                }}
              >
                {t('public.pricing.features_table.title_prefix')}{' '}
                <span
                  style={{
                    background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {t('public.pricing.features_table.title_highlight')}
                </span>
              </h2>
              <p
                style={{
                  color: 'rgba(255,255,255,0.35)',
                  fontSize: 'clamp(0.8rem, 2.4vw, 1rem)',
                  margin: '0 auto',
                  lineHeight: 1.4,
                }}
              >
                {t('public.pricing.features_table.subtitle')}
              </p>
            </div>

            <div className="flex flex-col lg:grid lg:grid-cols-[2fr_1fr_1fr] gap-6 lg:gap-8 lg:items-end">
              <div className="flex justify-center w-full lg:justify-start lg:block">
                <div className="inline-flex items-center rounded-full p-1 border w-fit" style={{ borderColor: '#d1d5db' }}>
                  <button
                    type="button"
                    className={`relative px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-full focus:outline-none ${
                      billingInterval === 'monthly'
                        ? 'toggle-button-active toggle-monthly-active'
                        : 'toggle-button-inactive'
                    }`}
                    style={{ 
                      border: 'none',
                      outline: 'none',
                      ...(billingInterval === 'monthly' ? {
                        background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                        } : {})
                    }}
                    onClick={() => setBillingInterval('monthly')}
                  >
                    {t('public.pricing.toggle.monthly')}
                  </button>
                  <button
                    type="button"
                    className={`relative px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-full focus:outline-none ${
                      billingInterval === 'yearly'
                        ? 'toggle-button-active toggle-yearly-active'
                        : 'toggle-button-inactive'
                    }`}
                    style={{ 
                      border: 'none',
                      outline: 'none',
                      ...(billingInterval === 'yearly' ? {
                        background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                        } : {})
                    }}
                    onClick={() => setBillingInterval('yearly')}
                  >
                    {t('public.pricing.toggle.yearly_full')}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 lg:contents">
              {/* Colonne Basic */}
              <div className="text-center lg:text-center">
                <h3 className="text-lg sm:text-xl font-bold mb-2" style={{ color: '#454653' }}>BASIC</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-3xl sm:text-4xl font-extrabold leading-none" style={{ color: '#454653' }}>
                    {t('public.pricing.plans.free_label')}
                  </span>
                </div>
              </div>

              {/* Colonne Premium */}
              <div className="text-center lg:text-center">
                <h3 className="text-lg sm:text-xl font-bold mb-2">
                  <span 
                    style={{
                      backgroundImage: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >PREMIUM</span>
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span 
                    className="text-3xl sm:text-4xl font-extrabold leading-none"
                    style={{
                      backgroundImage: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {formatEuro(PRICING_PREMIUM_AMOUNTS[billingInterval === 'yearly' ? 'yearly' : 'monthly'])}
                  </span>
                  <span className="text-base sm:text-lg font-bold" style={{ color: '#9ca3af' }}>{t('public.pricing.plans.per_month_suffix')}</span>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>

        {PRICING_FEATURE_SECTIONS.map((section, sectionIndex) => (
          <div
            key={section.title}
            className={`max-w-6xl mx-auto pl-12 pr-4 sm:px-6 md:px-12 lg:px-24${sectionIndex > 0 ? ' mt-20' : ''}`}
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-8">
              <span style={{ color: '#ffffff' }}>{section.title}</span>
            </h2>

            <div className="relative">
              <div className="relative z-10">
                {section.rows.map((row, rowIndex) => {
                  const isLast = rowIndex === section.rows.length - 1;
                  return (
                    <div
                      key={row.title}
                      className={`grid grid-cols-[minmax(0,1fr)_4.75rem_5rem] sm:grid-cols-[2fr_1fr_1fr] gap-2 sm:gap-8 items-center py-6${isLast ? '' : ' border-b'}`}
                      style={{ borderColor: '#e5e7eb' }}
                    >
                      <div className="flex min-w-0 flex-col pr-1 sm:pr-0">
                        <h3 className="text-sm sm:text-base font-semibold mb-1" style={{ color: '#454653' }}>{row.title}</h3>
                        {row.description && (
                          <p className="text-xs sm:text-sm" style={{ color: '#9ca3af' }}>{row.description}</p>
                        )}
                      </div>
                      <div className="flex min-w-0 w-full items-center justify-center text-center">
                        {renderPricingFeatureCell(row.basic, t)}
                      </div>
                      <div className="flex min-w-0 w-full items-center justify-center text-center">
                        {renderPricingFeatureCell(row.premium, t)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

      </div>

    {/* Section FAQ et CTA */}
    <section style={{ background: '#000000', width: '100%', padding: '0', margin: '0' }}>
      <div style={{ backgroundColor: '#000000', width: '100%', paddingTop: '4rem' }}>
        <style>{`
          #pricing-faq-section {
            background-color: #000000 !important;
          }

          #pricing-faq-section .bg-gray-100 {
            background: rgba(255,255,255,0.02) !important;
            border-color: transparent !important;
          }

          #pricing-faq-section h3,
          #pricing-faq-section p,
          #pricing-faq-section li {
            color: rgba(255,255,255,0.82) !important;
          }

          #pricing-faq-section p.pricing-faq-cta-note {
            color: rgba(255,255,255,0.4) !important;
          }
          
          .faq-open {
            animation: fadeInDown 0.4s ease-out forwards;
          }
          
          .faq-closed {
            animation: fadeOutUp 0.3s ease-out forwards;
          }
          
          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeOutUp {
            from {
              opacity: 1;
              transform: translateY(0);
            }
            to {
              opacity: 0;
              transform: translateY(-10px);
            }
          }
        `}</style>

        <div className="max-w-[1920px] mx-auto relative" style={{ backgroundColor: '#000000' }}>
        <div className="px-4 sm:px-6 md:px-12 lg:px-24" style={{ backgroundColor: '#000000', display: 'flex', flexDirection: 'column' }}>
        <div style={{ order: 2 }}>
        {/* FAQ Section */}
        <div id="pricing-faq-section" className="mt-8 sm:mt-12 mb-12 sm:mb-20 max-w-4xl mx-auto" style={{ backgroundColor: '#000000' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 20px' }}>{t('public.pricing.faq.eyebrow')}</p>
            <h2 style={{ color: '#FFFFFF', fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', fontWeight: 800, letterSpacing: '-0.04em', margin: '0 0 12px' }}>{t('public.pricing.faq.title')}</h2>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
              {t('public.pricing.faq.subtitle')}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Question 1 */}
            <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '14px', border: 'none', overflow: 'hidden' }}>
              <button
                onClick={() => {
                  setOpenFaqIndex(openFaqIndex === 0 ? null : 0);
                  trackFAQ('Que se passe-t-il à la fin de mes 7 jours d\'essai Premium ?');
                }}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px' }}
              >
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 600 }}>{t('public.pricing.faq.items.0.question')}</span>
                <svg
                  style={{ flexShrink: 0, transform: openFaqIndex === 0 ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} width="20" height="20" viewBox="0 0 20 20" fill="none"
                >
                  <path d="M10 4V16M4 10H16" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ${openFaqIndex === 0 ? 'faq-open' : 'faq-closed'}`}
                style={{
                  maxHeight: openFaqIndex === 0 ? '500px' : '0',
                  opacity: openFaqIndex === 0 ? 1 : 0
                }}
              >
                <div style={{ padding: '0 24px 20px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.88rem', lineHeight: 1.7, margin: 0 }}>
                    {t('public.pricing.faq.items.0.answer')}
                  </p>
                </div>
              </div>
            </div>

            {/* Question 2 */}
            <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '14px', border: 'none', overflow: 'hidden' }}>
              <button
                onClick={() => {
                  setOpenFaqIndex(openFaqIndex === 1 ? null : 1);
                  trackFAQ('Peux-tu changer de plan à tout moment ?');
                }}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px' }}
              >
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 600 }}>{t('public.pricing.faq.items.1.question')}</span>
                <svg style={{ flexShrink: 0, transform: openFaqIndex === 1 ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4V16M4 10H16" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ${openFaqIndex === 1 ? 'faq-open' : 'faq-closed'}`}
                style={{
                  maxHeight: openFaqIndex === 1 ? '500px' : '0',
                  opacity: openFaqIndex === 1 ? 1 : 0
                }}
              >
                <div style={{ padding: '0 24px 20px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.88rem', lineHeight: 1.7, margin: 0 }}>
                    {t('public.pricing.faq.items.1.answer')}
                  </p>
                </div>
              </div>
            </div>

            {/* Question 3 */}
            <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '14px', border: 'none', overflow: 'hidden' }}>
              <button
                onClick={() => {
                  setOpenFaqIndex(openFaqIndex === 2 ? null : 2);
                  trackFAQ('Que se passe-t-il si tu annules ton abonnement ?');
                }}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px' }}
              >
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 600 }}>{t('public.pricing.faq.items.2.question')}</span>
                <svg style={{ flexShrink: 0, transform: openFaqIndex === 2 ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4V16M4 10H16" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ${openFaqIndex === 2 ? 'faq-open' : 'faq-closed'}`}
                style={{
                  maxHeight: openFaqIndex === 2 ? '500px' : '0',
                  opacity: openFaqIndex === 2 ? 1 : 0
                }}
              >
                <div style={{ padding: '0 24px 20px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.88rem', lineHeight: 1.7, margin: 0 }}>
                    {t('public.pricing.faq.items.2.answer')}
                  </p>
                </div>
              </div>
            </div>

            {/* Question 4 */}
            <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '14px', border: 'none', overflow: 'hidden' }}>
              <button
                onClick={() => {
                  setOpenFaqIndex(openFaqIndex === 3 ? null : 3);
                  trackFAQ('Quels brokers sont supportés ?');
                }}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px' }}
              >
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 600 }}>{t('public.pricing.faq.items.3.question')}</span>
                <svg style={{ flexShrink: 0, transform: openFaqIndex === 3 ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4V16M4 10H16" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ${openFaqIndex === 3 ? 'faq-open' : 'faq-closed'}`}
                style={{
                  maxHeight: openFaqIndex === 3 ? '500px' : '0',
                  opacity: openFaqIndex === 3 ? 1 : 0
                }}
              >
                <div style={{ padding: '0 24px 20px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.88rem', lineHeight: 1.7, margin: 0 }}>
                    {t('public.pricing.faq.items.3.answer')}
                  </p>
                </div>
              </div>
            </div>

            {/* Question 5 */}
            <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '14px', border: 'none', overflow: 'hidden' }}>
              <button
                onClick={() => {
                  setOpenFaqIndex(openFaqIndex === 4 ? null : 4);
                  trackFAQ('Les données sont-elles sécurisées ?');
                }}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px' }}
              >
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 600 }}>{t('public.pricing.faq.items.4.question')}</span>
                <svg style={{ flexShrink: 0, transform: openFaqIndex === 4 ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4V16M4 10H16" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ${openFaqIndex === 4 ? 'faq-open' : 'faq-closed'}`}
                style={{
                  maxHeight: openFaqIndex === 4 ? '500px' : '0',
                  opacity: openFaqIndex === 4 ? 1 : 0
                }}
              >
                <div style={{ padding: '0 24px 20px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.88rem', lineHeight: 1.7, margin: 0 }}>
                    {t('public.pricing.faq.items.4.answer')}
                  </p>
                </div>
              </div>
            </div>

            {/* Question 6 */}
            <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '14px', border: 'none', overflow: 'hidden' }}>
              <button
                onClick={() => {
                  setOpenFaqIndex(openFaqIndex === 5 ? null : 5);
                  trackFAQ('L\'IA fonctionne-t-elle pour tous les styles de trading ?');
                }}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px' }}
              >
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 600 }}>{t('public.pricing.faq.items.5.question')}</span>
                <svg style={{ flexShrink: 0, transform: openFaqIndex === 5 ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4V16M4 10H16" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round"/></svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ${openFaqIndex === 5 ? 'faq-open' : 'faq-closed'}`}
                style={{
                  maxHeight: openFaqIndex === 5 ? '500px' : '0',
                  opacity: openFaqIndex === 5 ? 1 : 0
                }}
              >
                <div style={{ padding: '0 24px 20px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.88rem', lineHeight: 1.7, margin: 0 }}>
                    {t('public.pricing.faq.items.5.answer')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: '56px' }}>
            <h3 style={{ color: '#FFFFFF', fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 24px' }}>
              {t('public.pricing.faq.bottom_title')}
            </h3>
            <button
              onClick={handlePremiumCta}
              onMouseEnter={() => trackCTAHover('faq-cta', true)}
              onMouseLeave={() => trackCTAHover('faq-cta', false)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '16px 40px', borderRadius: '100px', background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', color: '#fff', fontSize: '1rem', fontWeight: 700, border: 'none', cursor: 'pointer', letterSpacing: '-0.02em', boxShadow: '0 0 60px rgba(139,92,246,0.3)' }}
            >
              {t('landing.common.cta_analyze_free')}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <p className="text-xs mt-6 text-center pricing-faq-cta-note" style={{ color: 'rgba(255,255,255,0.15)' }}>
              {t('public.pricing.faq.bottom_note')}
            </p>
          </div>
        </div>
        </div>

        <div style={{ order: 1 }}>
        {(() => {
          const total = pricingPartners.length;
          const mobilePartnersLoop = [...pricingPartners, ...pricingPartners, ...pricingPartners];
          const mobileTrackIndex = partnerMobileIndex || (total + partnerActive);
          const reviewLoop = [...pricingReviews, ...pricingReviews];
          return (
            <section style={{ background: '#040405', padding: '80px 0 96px', position: 'relative', overflow: 'hidden', marginTop: '-1px', width: '100vw', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw' }}>
              <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '1px', height: '1px', boxShadow: '0 0 500px 180px rgba(139,92,246,0.06)', pointerEvents: 'none' }} />
              <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24" style={{ position: 'relative', zIndex: 1 }}>
                <AnimatedText delay={0} duration={700}>
                  <div style={{ maxWidth: '760px', margin: '0 auto 48px', textAlign: 'center', padding: '0 24px' }}>
                    <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 20px' }}>{t('public.pricing.partners.eyebrow')}</p>
                    <h2 style={{ color: '#FFFFFF', fontSize: 'clamp(2rem, 4vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 20px' }}>{t('public.pricing.partners.title_prefix')} <span style={{ background: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('public.pricing.partners.title_highlight')}</span></h2>
                    <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '1rem', maxWidth: '520px', margin: '0 auto', lineHeight: 1.7 }}>{t('public.pricing.partners.subtitle')}</p>
                  </div>
                </AnimatedText>
                <AnimatedText delay={90} duration={700}>
                  <div className="hidden sm:block">
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '520px' }}>
                      {pricingPartners.map((p, i) => {
                        const isActive = i === partnerActive;
                        return <div key={p.id} onMouseEnter={() => { if (partnerTimerRef.current) clearInterval(partnerTimerRef.current); setPartnerActive(i); }} onClick={() => partnerGoTo(i)} style={{ flex: '1', height: isActive ? '520px' : '380px', transition: 'height 0.65s cubic-bezier(0.4,0,0.2,1)', position: 'relative', cursor: 'pointer' }}>
                          {p.isPrimary && <div style={{ position: 'absolute', top: '-14px', left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 10, opacity: isActive ? 1 : 0, transition: 'opacity 0.35s ease' }}><span style={{ background: '#0B0B0C', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '100px', padding: '5px 16px', color: '#ffffff', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{t('public.pricing.partners.primary_badge')}</span></div>}
                          <div style={{ position: 'absolute', inset: 0, borderRadius: '18px', overflow: 'hidden', border: '1px solid #0B0B0C' }}>
                            <picture><source srcSet={p.localPhotoWebP} type="image/webp" /><img src={p.localPhoto} alt={p.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} /></picture>
                            <div style={{ position: 'absolute', inset: 0, background: isActive ? 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)' : 'rgba(0,0,0,0.6)', transition: 'background 0.65s ease' }} />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '16px', opacity: isActive ? 0 : 1, transition: 'opacity 0.35s ease', pointerEvents: 'none' }}><p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.78rem', fontWeight: 700, textAlign: 'center', margin: 0 }}>{p.name}</p></div>
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '32px 28px', opacity: isActive ? 1 : 0, transform: isActive ? 'translateY(0)' : 'translateY(10px)', transition: 'opacity 0.45s ease 0.1s, transform 0.45s ease 0.1s', pointerEvents: 'none' }}><h3 style={{ color: '#ffffff', fontSize: 'clamp(1.3rem, 2vw, 1.7rem)', fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.03em', lineHeight: 1.1 }}>{p.name}</h3><p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', lineHeight: 1.6, margin: '0 0 12px', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.bio}</p><p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', margin: 0 }}><span style={{ color: '#ffffff', fontWeight: 800, fontSize: '1.4rem', letterSpacing: '-0.02em' }}>{formatSubs(p.subscribers)}</span> {t('public.pricing.partners.subscribers_suffix')}</p></div>
                          </div>
                        </div>;
                      })}
                    </div>
                  </div>
                  <div className="sm:hidden" style={{ overflow: 'hidden', margin: '0 -16px' }}>
                    <div style={{ display: 'flex', gap: '12px', transform: `translateX(calc(48px - ${mobileTrackIndex} * (100vw - 84px)))`, transition: partnerMobileTransitionEnabled ? 'transform 0.55s cubic-bezier(0.4,0,0.2,1)' : 'none', willChange: 'transform' }}>
                      {mobilePartnersLoop.map((p, i) => {
                        const actualIndex = i % total;
                        const isCardActive = i === mobileTrackIndex;
                        return <div key={`${p.id}-${i}`} onClick={() => partnerGoTo(actualIndex)} style={{ minWidth: 'calc(100vw - 96px)', borderRadius: '16px', overflow: 'hidden', border: isCardActive ? '1px solid rgba(167,139,250,0.2)' : '1px solid rgba(255,255,255,0.06)', background: '#141416', opacity: isCardActive ? 1 : 0.4, transform: isCardActive ? 'scale(1)' : 'scale(0.96)', transition: 'opacity 0.55s ease, transform 0.55s ease', cursor: 'pointer', display: 'flex', flexDirection: 'column' }}><div style={{ width: '100%', aspectRatio: '3/3.7', position: 'relative', overflow: 'hidden', background: '#141416' }}><picture><source srcSet={p.localPhotoWebP} type="image/webp" /><img src={p.localPhoto} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} /></picture><div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%', background: 'linear-gradient(to top,rgba(0,0,0,0.8),transparent)' }} />{p.isPrimary && <div style={{ position: 'absolute', top: '14px', left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}><span style={{ background: 'rgba(0,0,0,0.5)', borderRadius: '100px', padding: '5px 14px', color: '#ffffff', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{t('public.pricing.partners.primary_badge')}</span></div>}</div><div style={{ background: '#141416', padding: '16px 16px 18px', flex: 1 }}><h3 style={{ color: '#ffffff', fontSize: '1.05rem', fontWeight: 800, margin: '0 0 6px', letterSpacing: '-0.02em', lineHeight: 1.1 }}>{p.name}</h3><p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', lineHeight: 1.55, margin: '0 0 10px' }}>{p.bio}</p><p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', margin: 0 }}><span style={{ color: '#ffffff', fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.02em' }}>{formatSubs(p.subscribers)}</span> {t('public.pricing.partners.subscribers_suffix')}</p></div></div>;
                      })}
                    </div>
                  </div>
                </AnimatedText>
                <AnimatedText delay={160} duration={700}>
                  <div style={{ marginTop: '80px', textAlign: 'center' }}>
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', fontWeight: 500, margin: '0 0 16px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('public.pricing.partners.approved_label')}</p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '6px' }}><img src={trustpilotLogo} alt="Trustpilot stars" style={{ height: 20, width: 'auto' }} /><span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700 }}>{t('public.pricing.hero.trustpilot_rating')}</span></div>
                    <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.83rem', margin: 0 }}>{t('public.pricing.partners.reviews_count')}</p>
                    <div style={{ position: 'relative', width: '100%', overflow: 'hidden', marginTop: '56px', maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)' }}>
                      <div style={{ display: 'flex', gap: '16px', width: 'max-content', animation: 'partnerReviewScroll 30s linear infinite' }}>
                        {reviewLoop.map((t, i) => <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '16px 18px', width: '240px', flexShrink: 0, textAlign: 'left' }}><div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}><div style={{ position: 'relative', flexShrink: 0 }}><img src={t.img} alt={t.name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', display: 'block' }} /><svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', bottom: -2, right: -2, width: 12, height: 12 }}><path fillRule="evenodd" clipRule="evenodd" d="M9.5924 3.20027C9.34888 3.4078 9.22711 3.51158 9.09706 3.59874C8.79896 3.79854 8.46417 3.93721 8.1121 4.00672C7.95851 4.03705 7.79903 4.04977 7.48008 4.07522C6.6787 4.13918 6.278 4.17115 5.94371 4.28923C5.17051 4.56233 4.56233 5.17051 4.28923 5.94371C4.17115 6.278 4.13918 6.6787 4.07522 7.48008C4.04977 7.79903 4.03705 7.95851 4.00672 8.1121C3.93721 8.46417 3.79854 8.79896 3.59874 9.09706C3.51158 9.22711 3.40781 9.34887 3.20027 9.5924C2.67883 10.2043 2.4181 10.5102 2.26522 10.8301C1.91159 11.57 1.91159 12.43 2.26522 13.1699C2.41811 13.4898 2.67883 13.7957 3.20027 14.4076C3.40778 14.6511 3.51158 14.7729 3.59874 14.9029C3.79854 15.201 3.93721 15.5358 4.00672 15.8879C4.03705 16.0415 4.04977 16.201 4.07522 16.5199C4.13918 17.3213 4.17115 17.722 4.28923 18.0563C4.56233 18.8295 5.17051 19.4377 5.94371 19.7108C6.278 19.8288 6.6787 19.8608 7.48008 19.9248C7.79903 19.9502 7.95851 19.963 8.1121 19.9933C8.46417 20.0628 8.79896 20.2015 9.09706 20.4013C9.22711 20.4884 9.34887 20.5922 9.5924 20.7997C10.2043 21.3212 10.5102 21.5819 10.8301 21.7348C11.57 22.0884 12.43 22.0884 13.1699 21.7348C13.4898 21.5819 13.7957 21.3212 14.4076 20.7997C14.6511 20.5922 14.7729 20.4884 14.9029 20.4013C15.201 20.2015 15.5358 20.0628 15.8879 19.9933C16.0415 19.963 16.201 19.9502 16.5199 19.9248C17.3213 19.8608 17.722 19.8288 18.0563 19.7108C18.8295 19.4377 19.4377 18.8295 19.7108 18.0563C19.8288 17.722 19.8608 17.3213 19.9248 16.5199C19.9502 16.201 19.963 16.0415 19.9933 15.8879C20.0628 15.5358 20.2015 15.201 20.4013 14.9029C20.4884 14.7729 20.5922 14.6511 20.7997 14.4076C21.3212 13.7957 21.5819 13.4898 21.7348 13.1699C22.0884 12.43 22.0884 11.57 21.7348 10.8301C21.5819 10.5102 21.3212 10.2043 20.7997 9.5924C20.5922 9.34887 20.4884 9.22711 20.4013 9.09706C20.2015 8.79896 20.0628 8.46417 19.9933 8.1121C19.963 7.95851 19.9502 7.79903 19.9248 7.48008C19.8608 6.6787 19.8288 6.278 19.7108 5.94371C19.4377 5.17051 18.8295 4.56233 18.0563 4.28923C17.722 4.17115 17.3213 4.13918 16.5199 4.07522C16.201 4.04977 16.0415 4.03705 15.8879 4.00672C15.5358 3.93721 15.201 3.79854 14.9029 3.59874C14.7729 3.51158 14.6511 3.40781 14.4076 3.20027C13.7957 2.67883 13.4898 2.41811 13.1699 2.26522C12.43 1.91159 11.57 1.91159 10.8301 2.26522C10.5102 2.4181 10.2043 2.67883 9.5924 3.20027ZM16.3735 9.86314C16.6913 9.5453 16.6913 9.03 16.3735 8.71216C16.0557 8.39433 15.5403 8.39433 15.2225 8.71216L10.3723 13.5624L8.77746 11.9676C8.45963 11.6498 7.94432 11.6498 7.62649 11.9676C7.30866 12.2854 7.30866 12.8007 7.62649 13.1186L9.79678 15.2889C10.1146 15.6067 10.6299 15.6067 10.9478 15.2889L16.3735 9.86314Z" fill="#00B67A"/></svg></div><span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', fontWeight: 600 }}>{t.name}</span></div><p style={{ color: '#fff', fontSize: '0.78rem', fontWeight: 700, margin: '0 0 6px', lineHeight: 1.3 }}>{t.title}</p><p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', lineHeight: 1.55, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.text}</p></div>)}
                      </div>
                    </div>
                    <p className="text-xs mt-6 text-center" style={{ color: 'rgba(255,255,255,0.15)' }}>{t('public.pricing.partners.testimonial_disclaimer')}</p>
                  </div>
                </AnimatedText>
              </div>
            </section>
          );
        })()}
        </div>

        </div>
      </div>
      </div>
    </section>
    
    {/* Point de visualisation des dead clicks pour le debug */}
    {debugClick && (
      <>
        <div
          style={{
            position: 'fixed',
            left: `${debugClick.x}px`,
            top: `${debugClick.y}px`,
            width: '30px',
            height: '30px',
            marginLeft: '-15px',
            marginTop: '-15px',
            borderRadius: '50%',
            backgroundColor: 'rgba(239, 68, 68, 0.8)',
            boxShadow: '0 0 20px rgba(239, 68, 68, 0.8)',
            animation: 'pulse 2s infinite',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'fixed',
            left: `${debugClick.x}px`,
            top: `${debugClick.y - 60}px`,
            marginLeft: '-80px',
            padding: '8px 16px',
            backgroundColor: 'rgba(239, 68, 68, 0.95)',
            color: 'white',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Dead Click Position ({debugClick.x}, {debugClick.y})
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.5);
              opacity: 0.5;
            }
          }
        `}</style>
      </>
    )}
    </>
  );
};

export default Pricing;
